import { DataListItem } from '@brighte/form-components';
import { FINANCE_PURCHASE } from '@utils';

export type ReviewSection = { title: string; items: DataListItem[] };
type LabelledList = { title: string; items: DataListItem[] };

const { REVIEW_HEADINGS, REVIEW_FIELDS, FINANCIAL_PRODUCT_FIELDS, VENDOR_FIELDS } = FINANCE_PURCHASE;

const byStringLabel = (items: DataListItem[]) =>
  new Map(
    items
      .filter((item): item is DataListItem & { label: string } => typeof item.label === 'string')
      .map(item => [item.label, item] as const),
  );

/**
 * "What you're purchasing" — the purchase items, with the product description (a label-only
 * item from getPurchaseDetails) surfaced as a "Description" row.
 */
export function buildWhatYourePurchasing(purchaseDetails: LabelledList): ReviewSection {
  return {
    title: REVIEW_HEADINGS.whatYourePurchasing,
    items: purchaseDetails.items.map(item =>
      item.value === undefined && typeof item.label === 'string'
        ? { label: REVIEW_FIELDS.description, value: item.label }
        : item,
    ),
  };
}

/** "Quote details" — the quote reference and expiry, split out from the installer block. */
export function buildQuoteDetails(vendorDetails: LabelledList): ReviewSection {
  const referenceItem = vendorDetails.items.find(item => item.label === VENDOR_FIELDS.referenceNumber);
  const expiresItem = vendorDetails.items.find(item => item.label === VENDOR_FIELDS.expiryDate);
  return {
    title: REVIEW_HEADINGS.quoteDetails,
    items: [
      // referenceItem keeps its original "Reference" label from getVendorDetails.
      ...(referenceItem ? [referenceItem] : []),
      ...(expiresItem ? [{ ...expiresItem, label: REVIEW_FIELDS.expires }] : []),
    ],
  };
}

/** "Your installer" — vendor name (was the block title) and contact details. */
export function buildYourInstaller(vendorDetails: LabelledList): ReviewSection {
  const phoneItem = vendorDetails.items.find(item => item.label === VENDOR_FIELDS.mobile);
  const emailItem = vendorDetails.items.find(item => item.label === VENDOR_FIELDS.email);
  return {
    title: REVIEW_HEADINGS.yourInstaller,
    items: [
      ...(vendorDetails.title ? [{ label: REVIEW_FIELDS.name, value: vendorDetails.title }] : []),
      ...(phoneItem ? [phoneItem] : []),
      ...(emailItem ? [emailItem] : []),
    ],
  };
}

/**
 * The full Brighte Pay customer-facing review structure, assembled by regrouping/relabelling the
 * existing assembler outputs (no new value derivation). Shared by the active finance step and the
 * completed-review summary so both stay identical. Installation address is intentionally excluded —
 * the active view renders an address input, the completed view appends its own address row.
 */
export function getBrightePayReviewSections({
  financeDetails,
  feesDetails,
  purchaseDetails,
  vendorDetails,
  productName,
  showFeesFootnote = false,
}: {
  financeDetails: DataListItem[];
  feesDetails: DataListItem[];
  purchaseDetails: LabelledList;
  vendorDetails: LabelledList;
  productName: string;
  /**
   * Adds the "2" marker to the Fees & charges heading, pairing it with the fee-change disclaimer that
   * StepFooter renders. Only the live finance step has that footer — the completed review does not, so
   * it leaves this off rather than showing a marker with nothing to point at.
   */
  showFeesFootnote?: boolean;
}): ReviewSection[] {
  const financeByLabel = byStringLabel(financeDetails);
  const feesByLabel = byStringLabel(feesDetails);

  const purchaseAmountItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.totalPurchaseAmount);
  const depositItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.depositAmountExact);
  // getFinanceDetails only emits this when promotionAmount > 0, so the lookup omits the row itself
  // when there is no promotion. Without it the price rows do not reconcile.
  const promotionItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.promotion);
  const financedItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.financedAmountExact);
  const loanTermItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.loanTerm);
  const firstRepaymentItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.amountOnFirstRepaymentDate);
  const fortnightlyItem = financeByLabel.get(FINANCIAL_PRODUCT_FIELDS.fortnightlyRepayments);

  const establishmentFeeItem = feesByLabel.get(FINANCIAL_PRODUCT_FIELDS.establishmentFee);
  const accountKeepingFeeItem = feesByLabel.get(FINANCIAL_PRODUCT_FIELDS.weeklyAccountFee);
  const latePaymentFeeItem = feesByLabel.get(FINANCIAL_PRODUCT_FIELDS.latePaymentFee);

  const firstRepaymentDescription =
    establishmentFeeItem?.value !== undefined
      ? `includes ${establishmentFeeItem.value} establishment fee`
      : firstRepaymentItem?.description;

  const price: ReviewSection = {
    title: REVIEW_HEADINGS.price,
    items: [
      ...(purchaseAmountItem ? [purchaseAmountItem] : []),
      ...(depositItem ? [{ ...depositItem, label: FINANCIAL_PRODUCT_FIELDS.deposit }] : []),
      ...(promotionItem ? [promotionItem] : []),
      ...(financedItem ? [{ ...financedItem, label: REVIEW_FIELDS.amountFinanced }] : []),
    ],
  };
  const repayments: ReviewSection = {
    title: REVIEW_HEADINGS.repayments,
    items: [
      { label: REVIEW_FIELDS.loanType, value: productName },
      ...(loanTermItem ? [loanTermItem] : []),
      // Spread rather than rebuild: the row carries symbol '1', which the StepFooter disclaimer
      // references. Rebuilding it dropped the marker and orphaned the footnote.
      ...(fortnightlyItem ? [{ ...fortnightlyItem, label: REVIEW_FIELDS.fortnightlyRepayment }] : []),
      ...(firstRepaymentItem
        ? [{ ...firstRepaymentItem, label: REVIEW_FIELDS.firstRepayment, description: firstRepaymentDescription }]
        : []),
    ],
  };
  // One cohesive Fees & charges table; the one-off / ongoing / if-applicable groupings are muted caption rows.
  const feesAndCharges: ReviewSection = {
    title: showFeesFootnote ? REVIEW_HEADINGS.feesAndChargesWithFootnote : REVIEW_HEADINGS.feesAndCharges,
    items: [
      { label: REVIEW_HEADINGS.oneOff, caption: true },
      ...(establishmentFeeItem ? [establishmentFeeItem] : []),
      { label: REVIEW_HEADINGS.ongoing, caption: true },
      ...(accountKeepingFeeItem
        ? [{ label: REVIEW_FIELDS.accountKeepingFee, value: `${accountKeepingFeeItem.value} / week` }]
        : []),
      { label: REVIEW_HEADINGS.ifApplicable, caption: true },
      ...(latePaymentFeeItem ? [latePaymentFeeItem] : []),
    ],
  };

  return [
    buildWhatYourePurchasing(purchaseDetails),
    price,
    repayments,
    feesAndCharges,
    buildQuoteDetails(vendorDetails),
    buildYourInstaller(vendorDetails),
  ];
}
